import { useAuthStore } from '@/stores/auth-store';
import { useAppVariant } from '@/lib/app-variant';
import { usePermissions } from '@/hooks/usePermissions';
import { ROLE_TYPES } from '@scio3/shared-types';

/**
 * Can this user reach the OptiSound tab?
 *
 * The same test TopNavigation uses to show the tab. It lives here because copy
 * and buttons elsewhere point AT that tab: the Background Music dialog told
 * every user to "manage the zone from the OptiSound tab" and offered a button
 * that navigated there, neither gated, so a non-Owner was sent to a route they
 * cannot use (the OptiSound API rejects non-admin embed callers).
 *
 * Duplicating seven clauses inline would drift, so both read this.
 */
export function useCanManageOptisound(): boolean {
  const company = useAuthStore((s) => s.company);
  const permissionsLoaded = useAuthStore((s) => s.me != null);
  const { isRootSuperAdmin, teamRole } = usePermissions();
  const variant = useAppVariant();

  const isRestrictedRole =
    teamRole === ROLE_TYPES.READ_ONLY ||
    teamRole === ROLE_TYPES.CONTENT_PROPOSAL ||
    teamRole === ROLE_TYPES.CONTENT_APPROVAL;

  const isWhiteLabel = !!company?.brandingData?.isWhiteLabel;
  const removeOptiSignsBranding = !!company?.brandingData?.removeOptiSignsBranding;
  const useNativeOptisound = import.meta.env.VITE_USE_NATIVE_OPTISOUND === 'true';

  // Its OWN trial or paid subscription — an expired add-on is neither.
  const backgroundMusicAddOn = company?.payment?.subscriptionIdAddOns?.find(
    (a) => a.key === 'backgroundmusic',
  );
  const hasActiveBackgroundMusic =
    !!backgroundMusicAddOn && (!!backgroundMusicAddOn.isActive || !!backgroundMusicAddOn.isTrial);

  return (
    permissionsLoaded &&
    !isRestrictedRole &&
    isRootSuperAdmin &&
    useNativeOptisound &&
    variant === 'optisigns' &&
    hasActiveBackgroundMusic &&
    (!isWhiteLabel || !removeOptiSignsBranding)
  );
}
